import type {
  LandingPagePreviewData,
  LpBlock,
  LpBlockField,
  LpPage,
  LpPhaseKey,
  LpPresetKey,
  LpStat,
  LpStatusKey,
  LpTestingDetail,
  LpTone,
  LpVariant,
} from '@elva/ui';
import { growthGet, growthList } from '@/data/http';
import { campaignLpPhaseToFe } from '@/data/growthEnums';
import { deepCamel, num, relativeTimeFromIso, str } from '@/data/adapt';
import { enumLower } from '@/data/adapt';

export const LANDING_PAGES_PAGE_SIZE = 25;

export interface LandingPagesData {
  items: LpPage[];
  stats: LpStat[];
  archivedCount: number;
}

const STAT_TONE_BY_KEY: Record<string, LpTone> = {
  live: 'success',
  pending: 'gold',
  pending_approval: 'gold',
  testing: 'info',
  paused: 'neutral',
  cr: 'success',
  conversion_rate: 'success',
};

function mapPublishStatus(wire: string): LpStatusKey {
  const status = enumLower(wire, 'live' as string);
  if (status === 'pending_approval' || status === 'pending') return 'pending_approval';
  if (status === 'paused') return 'paused';
  if (status === 'archived') return 'archived';
  return 'live';
}

function mapPresetKey(wire: string): LpPresetKey {
  const key = enumLower(wire, 'code_red' as string);
  const allowed: LpPresetKey[] = [
    'code_red',
    'high_ticket',
    'transactional',
    'new_local',
    'offer_funnel',
    'anxiety',
    'family',
  ];
  return allowed.includes(key as LpPresetKey) ? (key as LpPresetKey) : 'code_red';
}

/** List sends ratio (0.1355); detail top-level performance sends percent (13.55). */
function normalizeConversionRate(raw: unknown, asRatio: boolean): number {
  const n = num(raw);
  if (!Number.isFinite(n) || n === 0) return 0;
  if (asRatio && n > 0 && n <= 1) return Math.round(n * 10000) / 100;
  return Math.round(n * 100) / 100;
}

function centsToDollars(cents: unknown): number {
  return Math.round(num(cents) / 100);
}

function daysSinceIso(iso: string): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

function daysUntilLabel(iso: string): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const days = Math.ceil((t - Date.now()) / 86400000);
  if (days <= 0) return 'soon';
  return `${days} day${days === 1 ? '' : 's'}`;
}

function resolveLeaderLabel(
  leader: string | null | undefined,
  winnerVariantId: unknown,
  variants?: LpVariant[],
): string | null {
  if (variants?.length && leader) {
    const match = variants.find((variant) => variant.id === leader);
    if (match) return match.label;
  }
  if (leader) {
    const stripped = leader.replace(/^lpv_/, '');
    return stripped !== leader ? stripped : leader;
  }
  if (winnerVariantId != null && String(winnerVariantId).trim()) {
    return String(winnerVariantId);
  }
  return null;
}

function adaptStatTone(key: string, tone?: string): LpTone {
  const normalized = str(tone, '').toLowerCase();
  if (normalized === 'success') return 'success';
  if (normalized === 'gold' || normalized === 'warning') return 'gold';
  if (normalized === 'terra' || normalized === 'error') return 'terra';
  if (normalized === 'info') return 'info';
  return STAT_TONE_BY_KEY[key.toLowerCase()] ?? 'neutral';
}

function adaptPerformance(
  raw: Record<string, unknown> | null | undefined,
  asRatio: boolean,
) {
  if (!raw) return null;
  const c = deepCamel<Record<string, unknown>>(raw);
  return {
    visits: num(c.visits),
    conversions: num(c.conversions),
    conversionRate: normalizeConversionRate(c.conversionRate, asRatio),
    formFills: num(c.formFills),
    calls: num(c.calls),
  };
}

function adaptTesting(
  raw: Record<string, unknown> | null | undefined,
  variants?: LpVariant[],
  itemRaw?: Record<string, unknown>,
): NonNullable<LpPage['testing']> | null {
  if (!raw && !itemRaw) return null;
  const c = deepCamel<Record<string, unknown>>(raw ?? {});
  const item = itemRaw ? deepCamel<Record<string, unknown>>(itemRaw) : {};
  const phase =
    campaignLpPhaseToFe(str(c.phase ?? item.testPhase, '')) ?? 'exploration';
  const leader = resolveLeaderLabel(
    (c.leader as string | null) ?? null,
    item.winnerVariantId ?? c.winnerVariantId,
    variants,
  );

  const testing: NonNullable<LpPage['testing']> = {
    phase: phase as LpPhaseKey,
    totalVariants: num(c.totalVariants, num(item.activeVariantCount)),
    activeVariants: num(c.activeVariants, num(item.activeVariantCount)),
    leader,
    budgetSpent: num(c.budgetSpentCents != null ? centsToDollars(c.budgetSpentCents) : c.budgetSpent),
  };
  const days = num(c.daysInExploitation, NaN);
  if (Number.isFinite(days)) testing.daysInExploitation = days;
  return testing;
}

function adaptLandingPage(row: Record<string, unknown>): LpPage {
  const r = deepCamel<Record<string, unknown>>(row);
  const id = str(r.id, '').trim();
  const title = str(r.name ?? r.title, '').trim();

  const campaignRaw =
    r.campaign && typeof r.campaign === 'object'
      ? deepCamel<Record<string, unknown>>(r.campaign)
      : null;
  const campaign = {
    name: str(campaignRaw?.name ?? r.campaignName, '—'),
    budget: centsToDollars(campaignRaw?.budgetCents ?? campaignRaw?.budget),
  };

  const createdAt = str(r.createdAt ?? r.created, '');
  const confidenceRaw = r.confidence;
  const publishStatus = str(r.publishStatus ?? r.status, 'live');
  const urlPath = str(r.urlPath ?? r.url, '').trim();

  const page: LpPage = {
    id,
    slug: str(r.externalId ?? r.slug, id),
    preset: mapPresetKey(str(r.presetKey ?? r.preset, 'code_red')),
    status: mapPublishStatus(publishStatus),
    title,
    url: urlPath || '—',
    campaign,
    blocks: num(r.blocks ?? r.activeVariantCount, 0),
    confidence: confidenceRaw == null ? null : num(confidenceRaw),
    created: createdAt ? relativeTimeFromIso(createdAt) : '—',
    performance: adaptPerformance(
      (r.performance as Record<string, unknown> | undefined) ?? null,
      true,
    ),
    testing: adaptTesting(
      (r.testing as Record<string, unknown> | undefined) ?? null,
      undefined,
      r,
    ),
  };
  if (r.pausedReason) page.pausedReason = str(r.pausedReason);
  return page;
}

function computeLandingPageStats(items: LpPage[]): LpStat[] {
  const live = items.filter((page) => page.status === 'live').length;
  const pending = items.filter((page) => page.status === 'pending_approval').length;
  const testing = items.filter(
    (page) => page.testing && page.testing.phase !== 'converged' && page.testing.activeVariants > 1,
  ).length;
  const withCr = items.filter((page) => page.performance && page.performance.conversionRate > 0);
  const avgCr =
    withCr.length > 0
      ? withCr.reduce((sum, page) => sum + (page.performance?.conversionRate ?? 0), 0) / withCr.length
      : 0;

  return [
    { key: 'live', label: 'Live pages', value: String(live), sub: '', tone: 'success' },
    { key: 'pending', label: 'Pending approval', value: String(pending), sub: '', tone: 'gold' },
    { key: 'testing', label: 'Active tests', value: String(testing), sub: '', tone: 'info' },
    {
      key: 'cr',
      label: 'Avg conversion rate',
      value: avgCr > 0 ? `${avgCr.toFixed(1)}%` : '—',
      sub: '',
      tone: 'success',
    },
  ];
}

function adaptSummaryStats(
  tiles: Array<Record<string, unknown>> | undefined,
  fallback: LpStat[],
): LpStat[] {
  if (!tiles?.length) return fallback;

  return tiles.map((tile) => {
    const c = deepCamel<Record<string, unknown>>(tile);
    const key = str(c.key ?? c.label, 'stat');
    return {
      key,
      label: str(c.label),
      value: c.value == null ? '—' : String(c.value),
      sub: str(c.sub, ''),
      tone: adaptStatTone(key, c.tone != null ? str(c.tone) : undefined),
    };
  });
}

export async function declareLandingPageWinner(id: string, variantId: string) {
  const { growthPostAsync } = await import('@/data/http');
  return growthPostAsync(`/landing-pages/${id}/declare-winner/`, { variant_id: variantId });
}

export async function retireLandingPageVariant(id: string, variantId: string) {
  const { growthPostAsync } = await import('@/data/http');
  return growthPostAsync(`/landing-pages/${id}/retire-variant/`, { variant_id: variantId });
}

export async function refreshLandingPageTest(id: string) {
  const { growthPostAsync } = await import('@/data/http');
  return growthPostAsync(`/landing-pages/${id}/refresh-test/`);
}

export function blocksToWirePayload(blocks: LpBlock[]): Record<string, unknown>[] {
  return blocks.map((block) => ({
    type: block.type,
    variant: block.variant,
    fields: block.fields.map((field) => {
      const wire: Record<string, unknown> = {
        key: field.key,
        label: field.label,
        value: field.value,
        type: field.type,
      };
      if (field.note) wire.note = field.note;
      if (field.maxLen != null) wire.max_len = field.maxLen;
      if (field.options?.length) wire.options = field.options;
      if (field.variesAcrossVariants != null) {
        wire.varies_across_variants = field.variesAcrossVariants;
      }
      return wire;
    }),
  }));
}

export async function patchLandingPageBlocks(id: string, blocks: Record<string, unknown>[]) {
  const { growthPatch } = await import('@/data/http');
  return growthPatch(`/landing-pages/${id}/blocks/`, { blocks });
}

export async function pauseLandingPage(id: string) {
  const { growthPost } = await import('@/data/http');
  return growthPost(`/landing-pages/${id}/pause/`);
}

/** Direct create from the LP editor shell. Returns 201 with landing_page_id. */
export async function createLandingPage(body: {
  title?: string;
  preset?: string;
  blocks?: Record<string, unknown>[];
}) {
  const { growthPostAsync } = await import('@/data/http');
  return growthPostAsync('/landing-pages/', {
    title: body.title,
    preset: body.preset,
    blocks: body.blocks,
  });
}

export async function fetchLandingPages(filters?: {
  status?: string;
  campaign?: string;
}): Promise<LandingPagesData> {
  const query: Record<string, string | number> = { limit: 100 };
  if (filters?.status) query.status = filters.status;
  // BE: campaign= exact campaign id (camp_… or numeric), not name.
  if (filters?.campaign) query.campaign = filters.campaign;

  const { items: rows } = await growthList<Record<string, unknown>>('/landing-pages/', query);

  let mapped = rows.map(adaptLandingPage);

  // BE omits archived when status is omitted; keep a light safety net only.
  if (!filters?.status) {
    mapped = mapped.filter((page) => page.status !== 'archived');
  }

  const archivedCount = mapped.filter((page) => page.status === 'archived').length;
  const fallbackStats = computeLandingPageStats(mapped);

  let stats = fallbackStats;
  let summaryArchived = archivedCount;
  try {
    const summary = await growthGet<{
      tiles?: Array<Record<string, unknown>>;
      archived_count?: number;
    }>('/landing-pages/summary/');
    stats = adaptSummaryStats(summary.tiles, fallbackStats);
    if (summary.archived_count != null) summaryArchived = num(summary.archived_count);
  } catch {
    // Summary-only KPIs
  }

  return {
    items: mapped,
    stats,
    archivedCount: summaryArchived,
  };
}

function adaptBlockField(row: Record<string, unknown>): LpBlockField {
  const f = deepCamel<Record<string, unknown>>(row);
  const field: LpBlockField = {
    key: str(f.key),
    label: str(f.label),
    value: (f.value as string | number | boolean) ?? '',
    type: (enumLower(f.type as string, 'text' as string) as LpBlockField['type']) ?? 'text',
  };
  if (f.note) field.note = str(f.note);
  if (f.maxLen != null) field.maxLen = num(f.maxLen);
  if (Array.isArray(f.options)) field.options = f.options.map((o) => str(o));
  if (f.variesAcrossVariants != null) field.variesAcrossVariants = Boolean(f.variesAcrossVariants);
  return field;
}

function adaptBlock(row: Record<string, unknown>, index: number): LpBlock {
  const b = deepCamel<Record<string, unknown>>(row);
  const fields = (b.fields as Record<string, unknown>[] | undefined) ?? [];
  return {
    id: str(b.id, `block-${index}`),
    type: str(b.type),
    variant: str(b.variant, '—'),
    fields: fields.map(adaptBlockField),
  };
}

function adaptVariant(row: Record<string, unknown>): LpVariant {
  const v = deepCamel<Record<string, unknown>>(row);
  const wireStatus = enumLower(v.status as string, 'active' as string);
  const variant: LpVariant = {
    id: str(v.id),
    label: str(v.label, str(v.id)),
    status: wireStatus === 'retired' ? 'retired' : 'active',
    isOriginal: Boolean(v.isOriginal),
    isWinner: Boolean(v.isWinner) || wireStatus === 'winner',
    strategy: str(v.strategy, '—'),
    headline: str(v.headline, '—'),
    cta: str(v.cta, '—'),
    visits: num(v.visits),
    conversions: num(v.conversions),
    cr: num(v.cr),
  };
  if (v.note) variant.note = str(v.note);
  return variant;
}

function adaptTestingDetail(
  itemRaw: Record<string, unknown>,
  variants: LpVariant[],
): LpTestingDetail {
  const item = deepCamel<Record<string, unknown>>(itemRaw);
  const testingRaw = deepCamel<Record<string, unknown>>(
    (itemRaw.testing as Record<string, unknown> | undefined) ?? {},
  );
  const phase =
    (campaignLpPhaseToFe(str(testingRaw.phase ?? item.testPhase, '')) ??
      'exploration') as LpTestingDetail['phase'];
  const winnerVariant = variants.find((variant) => variant.isWinner);
  const winner =
    winnerVariant?.label ??
    resolveLeaderLabel(
      (testingRaw.leader as string | null) ?? null,
      item.winnerVariantId,
      variants,
    ) ??
    '—';

  const phaseStarted = str(item.phaseStartedAt ?? testingRaw.currentRoundStarted, '');
  const nextRefresh = str(item.nextRefreshAt ?? testingRaw.nextRefreshIn, '');

  return {
    phase,
    totalVariants: num(testingRaw.totalVariants, variants.length),
    activeVariants: num(testingRaw.activeVariants, variants.filter((v) => v.status === 'active').length),
    budgetSpent: num(
      testingRaw.budgetSpentCents != null
        ? centsToDollars(testingRaw.budgetSpentCents)
        : testingRaw.budgetSpent,
    ),
    currentRoundStarted: phaseStarted ? relativeTimeFromIso(phaseStarted) : '—',
    nextRefreshIn: nextRefresh.includes('T') ? daysUntilLabel(nextRefresh) : str(nextRefresh, '—'),
    winner,
    variants,
  };
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
}

export async function fetchLandingPageDetail(id: string): Promise<LandingPagePreviewData> {
  const row = await growthGet<Record<string, unknown>>(`/landing-pages/${encodeURIComponent(id)}/`);
  const c = deepCamel<Record<string, unknown>>(row);
  const itemRaw = ((c.item ?? c) as Record<string, unknown>) ?? {};
  const item = deepCamel<Record<string, unknown>>(itemRaw);
  const metaRaw =
    c.meta && typeof c.meta === 'object'
      ? deepCamel<Record<string, unknown>>(c.meta as Record<string, unknown>)
      : item.meta && typeof item.meta === 'object'
        ? deepCamel<Record<string, unknown>>(item.meta as Record<string, unknown>)
        : {};

  const testingSource =
    (c.testing as Record<string, unknown> | undefined) ??
    (item.testing as Record<string, unknown> | undefined) ??
    {};
  const testingCamel = deepCamel<Record<string, unknown>>(testingSource);

  const presetKey = str(
    metaRaw.preset ?? item.presetKey ?? item.preset,
    'code_red',
  );
  const publishStatus = str(
    metaRaw.status ?? item.publishStatus ?? item.status,
    'live',
  );
  const urlPath = str(metaRaw.url ?? item.urlPath ?? item.url, '').trim();
  const createdAt = str(item.createdAt ?? item.created, '');
  const createdDays =
    metaRaw.createdDays != null ? num(metaRaw.createdDays) : daysSinceIso(createdAt);
  const campaignRaw =
    item.campaign && typeof item.campaign === 'object'
      ? deepCamel<Record<string, unknown>>(item.campaign)
      : null;

  const variantsRaw = asRecordArray(
    c.variants ?? testingCamel.variants ?? item.variants,
  );
  const variants = variantsRaw.map(adaptVariant);

  const topPerf = deepCamel<Record<string, unknown>>(
    (c.performance as Record<string, unknown> | undefined) ?? {},
  );
  const itemPerf = deepCamel<Record<string, unknown>>(
    (item.performance as Record<string, unknown> | undefined) ?? {},
  );

  const data: LandingPagePreviewData = {
    meta: {
      title: str(metaRaw.title ?? item.name ?? item.title),
      preset: presetKey,
      status: mapPublishStatus(publishStatus),
      url: urlPath || '—',
      campaign: str(metaRaw.campaign ?? campaignRaw?.name ?? item.campaign, '—'),
      createdDays,
    },
    performance: {
      visits: num(topPerf.visits, num(itemPerf.visits)),
      conversions: num(topPerf.conversions, num(itemPerf.conversions)),
      conversionRate: normalizeConversionRate(
        topPerf.conversionRate ?? itemPerf.conversionRate,
        false,
      ),
      formFills: num(itemPerf.formFills, num(topPerf.formFills)),
      calls: num(itemPerf.calls, num(topPerf.calls)),
      bounceRate: num(
        topPerf.bounceRate ?? topPerf.nonConversionRate ?? itemPerf.bounceRate,
      ),
      avgTimeSec: num(topPerf.avgTimeSec ?? itemPerf.avgTimeSec),
    },
  };

  const blocksRaw = asRecordArray(c.blocks ?? item.blocks);
  data.blocks = blocksRaw.map(adaptBlock);

  const testingItemRaw = {
    ...itemRaw,
    testing: testingSource,
  };
  data.testing = adaptTestingDetail(testingItemRaw, variants);

  return data;
}
